import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import TimelineSwitcher from '../Common/TimelineSwitcher';

const { FiBarChart, FiTrendingUp, FiTrendingDown, FiMinus } = FiIcons;

const VarianceAnalysis = ({ productId }) => {
  const [activeTable, setActiveTable] = useState('budget');

  // Mock budget vs actual data
  const budgetActualData = [
    {
      week: 'Week 1',
      budgetSales: 425000,
      actualSales: 448000,
      budgetVolume: 8500,
      actualVolume: 8920,
      variance: 5.4
    },
    {
      week: 'Week 2',
      budgetSales: 430000,
      actualSales: 398000,
      budgetVolume: 8600,
      actualVolume: 7960,
      variance: -7.4
    },
    {
      week: 'Week 3',
      budgetSales: 435000,
      actualSales: 452000,
      budgetVolume: 8700,
      actualVolume: 9040,
      variance: 3.9
    },
    {
      week: 'Week 4',
      budgetSales: 440000,
      actualSales: 463000,
      budgetVolume: 8800,
      actualVolume: 9260,
      variance: 5.2
    }
  ];

  // Mock customer forecast data
  const customerForecastData = [
    {
      customer: 'RedOak Sourcing',
      forecastedVolume: 125000,
      actualVolume: 142000,
      variance: 13.6,
      status: 'Above Forecast'
    },
    {
      customer: 'WBAD',
      forecastedVolume: 98000,
      actualVolume: 89500,
      variance: -8.7,
      status: 'Below Forecast'
    },
    {
      customer: 'ClarusOne',
      forecastedVolume: 75000,
      actualVolume: 78200,
      variance: 4.3,
      status: 'On Target'
    },
    {
      customer: 'Kaiser Permanente',
      forecastedVolume: 112000,
      actualVolume: 108500,
      variance: -3.1,
      status: 'Slight Underperformance'
    },
    {
      customer: 'Morris & Dickson',
      forecastedVolume: 65000,
      actualVolume: 71800,
      variance: 10.5,
      status: 'Above Forecast'
    },
    {
      customer: 'Optisource',
      forecastedVolume: 58000,
      actualVolume: 52300,
      variance: -9.8,
      status: 'Below Forecast'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getVarianceStatus = (variance) => {
    if (variance > 5) return { text: 'Positive', color: 'text-green-600', bgColor: 'bg-green-100', icon: FiTrendingUp };
    if (variance < -5) return { text: 'Negative', color: 'text-red-600', bgColor: 'bg-red-100', icon: FiTrendingDown };
    return { text: 'Neutral', color: 'text-gray-600', bgColor: 'bg-gray-100', icon: FiMinus };
  };

  const getCustomerStatus = (status) => {
    const statusMap = {
      'Above Forecast': { color: 'text-green-800', bgColor: 'bg-green-100' },
      'Below Forecast': { color: 'text-red-800', bgColor: 'bg-red-100' },
      'On Target': { color: 'text-blue-800', bgColor: 'bg-blue-100' },
      'Slight Underperformance': { color: 'text-yellow-800', bgColor: 'bg-yellow-100' }
    };
    return statusMap[status] || { color: 'text-gray-800', bgColor: 'bg-gray-100' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <SafeIcon icon={FiBarChart} className="text-blue-600" />
          Variance Analysis Reports
        </h3>
        
        {/* Table Switcher */}
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTable('budget')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTable === 'budget'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Budget vs Actual Weekly Analysis
          </button>
          <button
            onClick={() => setActiveTable('forecast')}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              activeTable === 'forecast'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Customer Forecast Analysis
          </button>
        </div>
      </motion.div>

      {/* Budget vs Actual Table */}
      {activeTable === 'budget' && (
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900">Budget vs Actual Weekly Report</h4>
            <p className="text-gray-600 mt-1">Weekly performance comparison against budget targets</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Week</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Budget Sales</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Actual Sales</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Budget Volume</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Actual Volume</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Variance %</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {budgetActualData.map((row, index) => {
                  const varianceStatus = getVarianceStatus(row.variance);
                  return (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6 font-medium text-gray-900">{row.week}</td>
                      <td className="py-4 px-6 text-right text-gray-700">{formatCurrency(row.budgetSales)}</td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">{formatCurrency(row.actualSales)}</td>
                      <td className="py-4 px-6 text-right text-gray-700">{formatNumber(row.budgetVolume)}</td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">{formatNumber(row.actualVolume)}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <SafeIcon icon={varianceStatus.icon} className={`text-sm ${varianceStatus.color}`} />
                          <span className={`font-medium ${varianceStatus.color}`}>
                            {row.variance > 0 ? '+' : ''}{row.variance}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${varianceStatus.bgColor} ${varianceStatus.color}`}>
                          {varianceStatus.text}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Customer Forecast Table */}
      {activeTable === 'forecast' && (
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.3 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
        >
          <div className="p-6 border-b border-gray-200">
            <h4 className="text-lg font-semibold text-gray-900">Customer Forecast Analysis</h4>
            <p className="text-gray-600 mt-1">Volume performance vs customer forecasts</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left py-4 px-6 font-semibold text-gray-900">Customer Name</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Forecasted Volume</th>
                  <th className="text-right py-4 px-6 font-semibold text-gray-900">Actual Volume</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Variance %</th>
                  <th className="text-center py-4 px-6 font-semibold text-gray-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {customerForecastData.map((row, index) => {
                  const varianceStatus = getVarianceStatus(row.variance);
                  const customerStatus = getCustomerStatus(row.status);
                  return (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-gray-900">{row.customer}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-right text-gray-700">{formatNumber(row.forecastedVolume)}</td>
                      <td className="py-4 px-6 text-right font-medium text-gray-900">{formatNumber(row.actualVolume)}</td>
                      <td className="py-4 px-6 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <SafeIcon icon={varianceStatus.icon} className={`text-sm ${varianceStatus.color}`} />
                          <span className={`font-medium ${varianceStatus.color}`}>
                            {row.variance > 0 ? '+' : ''}{row.variance}%
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-center">
                        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${customerStatus.bgColor} ${customerStatus.color}`}>
                          {row.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </motion.div>
      )}

      {/* Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-3 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">+4.3%</p>
              <p className="text-sm text-gray-600">Average Variance</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Overall performance above budget expectations</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">4/6</p>
              <p className="text-sm text-gray-600">Customers Above Forecast</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Strong customer demand performance</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">$1.76M</p>
              <p className="text-sm text-gray-600">Total Actual Sales</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Monthly sales performance</p>
        </div>
      </motion.div>
    </div>
  );
};

export default VarianceAnalysis;